import { ChiefComplaintSurvey } from '@chiroup/components/body/ChiefComplaintSurvey';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { SurveyDraft, SurveyDraftInput, SurveyQuestion } from '@chiroup/core/types/Survey.type';
import React, { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import requestService from '../../services/request.service';

// Adapter that mounts the legacy `<ChiefComplaintSurvey>` inside the
// healthcom-schedule app for surveys that contain a body picker
// (CCS, reevaluation, abbreviated CCS). The legacy component owns
// the entire bodypicker UX — anatomy diagram, problem-area picker,
// follow-up question chain — so wrapping it in the soft-card UI
// would just hide that. Instead we hand over the whole survey
// surface and submit through the legacy backend's whole-survey
// route, same auth model as the rest of the public request flow
// (validationCode in the body, no bearer).

type Props = {
  age: number;
  ccsFollowups?: string[];
  complete: () => void;
  gender: Genders;
  requestId: string;
  patientSurveyId: string;
  kiosk?: boolean;
  language: 'en' | 'es';
  startQuestion: SurveyQuestion;
  surveyIds?: string[];
  markSurveyComplete: () => void;
  updateRequest?: (
    data: {
      prMap: { [key: string]: string };
      steps: RequestStep[];
      language: 'en' | 'es';
      gender: Genders;
      age: number;
      id: string;
    },
    callback?: () => void
  ) => void;
  validationCode: string | number;
  prMap: { [key: string]: string };
  encounterId?: string;
  primaryColor: string;
  additionalComplaints?: boolean;
  abbreviatedCCS?: boolean;
  doNotAllowUnrelatedRegions?: boolean;
  customQuestions?: SurveyQuestion[];
  allowAdditionalComplaints?: boolean;
  customCCSSurveyId?: string;
  // Template id of the active survey. Required so an ACCS chain
  // re-queues the same (built-in or custom) template on extension.
  currentSurveyId?: string;
  draft?: SurveyDraft;
};

const InterCCS: React.FC<Props> = ({
  age,
  ccsFollowups,
  complete,
  gender,
  requestId,
  patientSurveyId,
  language,
  startQuestion,
  surveyIds,
  updateRequest,
  validationCode,
  prMap,
  markSurveyComplete,
  kiosk,
  encounterId,
  primaryColor,
  additionalComplaints = false,
  abbreviatedCCS = false,
  doNotAllowUnrelatedRegions = false,
  customQuestions,
  allowAdditionalComplaints = true,
  customCCSSurveyId,
  currentSurveyId,
  draft,
}: Props) => {
  // Tag every question in each problem area with a stable
  // complaintId so the backend can group them when persisting.
  // Mirrors the legacy `apps/healthcom-app/.../InterCCS.tsx` flow.
  const submit = (
    questions: SurveyQuestion[],
    kioskFlag: boolean,
    spanishQuestions?: SurveyQuestion[],
    ccsFollowupSurveyIds?: string[],
    validationCodeOverride?: string | number
  ) => {
    const id1 = uuid();
    const id2 = uuid();
    const id3 = uuid();

    const tagged = questions.map(q => {
      if (q.problemArea === 1) return { ...q, complaintId: id1 };
      if (q.problemArea === 2) return { ...q, complaintId: id2 };
      if (q.problemArea === 3) return { ...q, complaintId: id3 };

      return q;
    });

    return requestService.submitSurvey(
      requestId,
      patientSurveyId,
      tagged,
      kioskFlag,
      prMap,
      spanishQuestions,
      ccsFollowupSurveyIds,
      validationCodeOverride ?? validationCode,
      encounterId
    );
  };

  const saveDraft = useCallback(
    (nextDraft: SurveyDraftInput) =>
      requestService
        .saveSurveyDraft({
          requestId,
          patientSurveyId,
          draft: nextDraft,
          prMap,
          kiosk,
        })
        .then(() => undefined),
    [kiosk, patientSurveyId, prMap, requestId]
  );

  return (
    <ChiefComplaintSurvey
      language={language}
      gender={gender}
      age={age}
      primaryColor={primaryColor}
      startQuestion={startQuestion}
      complete={complete}
      ccsFollowups={ccsFollowups}
      markSurveyComplete={markSurveyComplete}
      updateRequest={updateRequest}
      surveyIds={surveyIds}
      validationCode={validationCode}
      prMap={prMap}
      submitSurvey={submit}
      kiosk={!!kiosk}
      additionalComplaints={additionalComplaints}
      abbreviatedCCS={abbreviatedCCS}
      doNotAllowUnrelatedRegions={doNotAllowUnrelatedRegions}
      customQuestions={customQuestions}
      allowAdditionalComplaints={allowAdditionalComplaints}
      customCCSSurveyId={customCCSSurveyId}
      currentSurveyId={currentSurveyId}
      draft={draft}
      saveDraft={saveDraft}
    />
  );
};

export default InterCCS;
